import { HierarchyValue } from 'RestAPIs/hierarchy';

export type HierarchyPlanUnit = HierarchyValue & { hierarchy: string[] };

export const createTreeHierarchy = (rows: HierarchyValue[]) => {
  const addHierarchy = (rows: HierarchyValue[]) => {
    const byDynId = new Map(rows.map((r) => [r.brDynId, r])); // O(1) lookup

    const findHierarchy = (
      row: HierarchyValue,
      path: string[] = [],
      visited: Set<number> = new Set()
    ): string[] => {
      // stop if the reference is broken or self-parenting
      if (!row.parentBrDynId || row.parentBrDynId === row.brDynId) return path;

      // cycle-check
      if (visited.has(row.parentBrDynId)) {
        console.warn(
          `[Hierarchy] cycle detected: ${[...visited, row.parentBrDynId].join(
            ' → '
          )}`
        );
        return path; // break the loop
      }

      const parent = byDynId.get(row.parentBrDynId);
      if (!parent) return path; // orphan → stop climbing

      visited.add(row.parentBrDynId); // mark and keep climbing
      path.push(parent.brId.toString());
      return findHierarchy(parent, path, visited);
    };

    return rows.map((row) => {
      const hierarchy = findHierarchy(row).reverse(); // root → child
      return {
        ...row,
        hierarchy: [...hierarchy, row.brId.toString()]
      };
    }) as HierarchyPlanUnit[];
  };

  return addHierarchy(rows);
};

export const getSelectedTreeHierarchy = (
  rows: HierarchyPlanUnit[],
  selectedRowId: string
) => {
  const findDescendants = (
    row: HierarchyPlanUnit,
    allRows: HierarchyPlanUnit[]
  ): HierarchyPlanUnit[] => {
    // Skip self-referencing rows
    if (row.brDynId === row.parentBrDynId) return [];

    const descendants = allRows.filter((r) => r.parentBrDynId === row.brDynId);

    return [
      {
        ...row,
        // The selected row should not have parents in its hierarchy, but only its own id or descendants
        hierarchy:
          selectedRowId === row.brId?.toString()
            ? [row.brId.toString()]
            : row.hierarchy
      },
      ...descendants
        .map((descendant) => ({
          ...descendant,
          // The descendats should only have their own id, the selected row id and the parents in their hierarchy
          hierarchy: descendant.hierarchy.filter(
            (id) =>
              id === descendant.brId.toString() ||
              id === selectedRowId ||
              row.brId.toString() === id
          )
        }))
        .flatMap((descendant) => findDescendants(descendant, allRows))
    ];
  };

  const selectedRow = rows.find((row) => row.brId.toString() === selectedRowId);
  if (!selectedRow) {
    return [];
  }
  return findDescendants(selectedRow, rows);
};

export const isDirectDescendant = (
  parentRow: HierarchyValue,
  childRow: HierarchyPlanUnit
) => {
  return childRow.parentBrDynId === parentRow.brDynId;
};

export const isDescendant = (
  parentRow: HierarchyValue,
  childRow: HierarchyPlanUnit
) => {
  return childRow.hierarchy?.includes(parentRow.brId?.toString());
};
